import React, { useEffect, useState } from 'react';

import { Alert, Badge, Button, Card, Col, Divider, Form, Row, Spin, Typography } from 'antd';
import { GetCheckoutUrl, GetCustomerPortalUrl, PlansResponse } from 'services/subscription.service';
import { Subscription, SubscriptionStatus } from 'redux/reducers/authReducer';
import Title from 'antd/lib/typography/Title';
import { useCurrentSubscription } from 'hooks';
import { RocketOutlined } from '@ant-design/icons';
import { ActionDefinition, CrudHeaderActions, ModalState, NewCrudHeader } from 'components/organism/crud';
import { usePlans, usePromo, useSavePlanChange } from './hooks';
import { RadioInput } from 'components/atom/form';
import { NewChangeOfferModal } from 'components/organism/subscriptions/change-offer-new-modal';
import { RED } from 'styles/colors';
import { useHistory } from 'react-router-dom';

declare global {
  interface Window {
    Stripe: any;
  }
}

interface PlanCheckoutProps {
  subscription: Partial<Subscription>;
  plans?: PlansResponse[];
  checkout: (offerPlanId: string, frequency: Frequency) => void;
}

enum Frequency {
  Monthly = 'Monthly',
  Yearly = 'Yearly',
}

function PriceTag(props: { oldPrice: number; price: number; label: string }) {
  const height = '50px';

  if (props.oldPrice === props.price) {
    return (
      <div style={{ height }}>
        <Typography>
          ${props.price} / {props.label}
        </Typography>
      </div>
    );
  }

  return (
    <div style={{ height }}>
      <Typography style={{ textDecoration: 'line-through', color: RED }}>
        ${props.oldPrice} / {props.label}
      </Typography>
      <Typography>
        ${props.price} / {props.label}
      </Typography>
    </div>
  );
}

const PlanCheckout = (props: PlanCheckoutProps) => {
  const currentSubscription = useCurrentSubscription();
  const [form] = Form.useForm();
  const [frequency, setFrequency] = useState(Frequency.Yearly);

  useEffect(() => {
    form.setFieldsValue({ frequency });
  }, []);

  if (currentSubscription.status !== SubscriptionStatus.Unsubscribed && currentSubscription.status !== SubscriptionStatus.Free) {
    return <></>;
  }

  if (!props.plans || !props.plans.length) {
    return <>No plans to subscribe.</>;
  }

  const getCard = (i: PlansResponse, frequency: Frequency) => {
    return (
      <Card hoverable={props.subscription.idPlanOffer !== i.id}>
        <Title level={4}>{i.name}</Title>
        <div>
          {frequency === Frequency.Monthly && <PriceTag oldPrice={i.originalMonthlyPrice} price={i.monthlyPrice} label="month" />}
          {frequency === Frequency.Yearly && <PriceTag oldPrice={i.originalYearlyPrice} price={i.yearlyPrice} label="year" />}
        </div>
        <Divider />
        <ul style={{ paddingInlineStart: 10 }}>
          {i.benefits.map((b) => (
            <li key={`${i.id}-${b}`}>{b}</li>
          ))}
        </ul>
        <Divider />
        <Button type="primary" style={{ marginTop: '10px' }} disabled={props.subscription.idPlanOffer === i.id} onClick={() => props.checkout(i.id, frequency)}>
          {props.subscription.idPlanOffer === i.id ? 'Subscribed' : 'Subscribe'}
        </Button>
      </Card>
    );
  };

  return (
    <>
      <Form form={form}>
        <Row>
          <RadioInput
            name="frequency"
            label="Billing frequency"
            optionsDirection="horizontal"
            options={[
              { value: Frequency.Monthly, display: Frequency.Monthly },
              { value: Frequency.Yearly, display: Frequency.Yearly },
            ]}
            onChange={(v) => setFrequency(v)}
          />
        </Row>
        <Row gutter={[16, 16]}>
          {props.plans.map((i) => (
            <Col key={i.id} xs={24} md={12} lg={6}>
              {i.highlight && (
                <Badge.Ribbon color="red" text={i.highlight}>
                  {getCard(i, frequency)}
                </Badge.Ribbon>
              )}
              {!i.highlight && getCard(i, frequency)}
            </Col>
          ))}
        </Row>
      </Form>
    </>
  );
};

const PlanInfo = () => {
  const [isLoading, setIsLoading] = useState(false);
  const currentSubscription = useCurrentSubscription();

  const portal = async () => {
    setIsLoading(true);
    const data = await GetCustomerPortalUrl();
    window.open(data.url, '_self');
    setIsLoading(false);
  };

  if (currentSubscription.status === SubscriptionStatus.Unsubscribed || currentSubscription.status === SubscriptionStatus.Free) {
    return <></>;
  }

  return (
    <>
      <Row className="subtitle">Bugal partners with Stripe for simplified billing.</Row>
      <div>
        The button below will allow you to:
        <br />
        <ul>
          <li>View your current subscription;</li>
          <li>Update your billing details; and</li>
          <li>View your billing history</li>
        </ul>
      </div>
      <Button disabled={isLoading} loading={isLoading} onClick={() => portal()}>
        Take me there
      </Button>
    </>
  );
};

export default function Index() {
  const currentSubscription = useCurrentSubscription();

  const [stripe, setStripe] = useState<any>();
  const [loading, setIsLoading] = useState(false);
  const [modalState, setModalState] = useState<ModalState>(ModalState.Hidden);

  const { data: plans, reloadList } = usePlans();
  const { isSaving, save, form } = useSavePlanChange();

  const checkout = async (priceId: string, frequency: Frequency) => {
    setIsLoading(true);
    await GetCheckoutUrl(priceId, frequency)
      .then((data) => {
        if (!data.id) {
          // Free version, skip stripe redirect
          window.open('/subscription/success', '_self');
          return;
        }
        stripe.redirectToCheckout({
          sessionId: data.id,
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const modalActions: ActionDefinition<any>[] = [
    {
      key: 'action-save',
      title: 'Save',
      icon: null,
      type: 'primary',
      canRender: () => modalState === ModalState.Edit,
      handler: async () => {
        try {
          const values = await form.validateFields();
          await save(values.code);
          setModalState(ModalState.Hidden);
          reloadList();
        } catch (e) {}
      },
    },
  ];

  useEffect(function initialiseStripe() {
    setStripe(window.Stripe(process.env.REACT_APP_STRIPE_API_KEY));
  }, []);

  const headerActions: CrudHeaderActions<any>[] = [
    // {
    //   key: '0',
    //   title: 'I have a promo code',
    //   icon: null,
    //   type: 'primary',
    //   handler: () => {
    //     setModalState(ModalState.Edit);
    //   },
    //   canRender: () => currentSubscription.isDefault,
    // },
  ];

  return (
    <Spin spinning={loading}>
      <NewCrudHeader title="Subscription" icon={<RocketOutlined />} actions={headerActions} />
      <div className="page-container">
        <PlanCheckout subscription={currentSubscription} plans={plans} checkout={checkout} />
        <PlanInfo />
        <NewChangeOfferModal form={form} actions={modalActions} saving={isSaving} modalState={modalState} setModalState={setModalState} />
      </div>
    </Spin>
  );
}

export function PlanOffer() {
  const subscription = useCurrentSubscription();
  const { data, loading } = usePromo();
  const { isSaving, save } = useSavePlanChange();
  const history = useHistory();

  if (subscription.status !== SubscriptionStatus.Unsubscribed && subscription.status !== SubscriptionStatus.Free) {
    return <></>;
  }

  const handleActivate = async () => {
    await save(data?.code!);
    history.push('/subscription');
    window.location.reload();
  };

  if (loading) {
    return <Spin spinning={loading} />;
  }

  if (!data) {
    return <></>;
  }

  return (
    <Alert
      message="Special Offer"
      type="warning"
      showIcon
      description={data.description}
      action={
        <Button size="small" type="link" onClick={handleActivate} disabled={isSaving}>
          Activate it now!
        </Button>
      }
    />
  );
}
